import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CancelPanelPagoAction } from '@/application/actions';
import { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import { PanelFacturacion } from '@/domain/entities/PanelFacturacion';
import { PanelPago } from '@/domain/entities/PanelPago';
import { createXlsxBlob, type XlsxCell, type XlsxCellAlign } from '@/shared/utils/xlsxExport';

interface PeriodDetailModalProps {
  isOpen: boolean;
  panel: PanelFacturacion;
  onClose: () => void;
}

export const PeriodDetailModal: React.FC<PeriodDetailModalProps> = ({
  isOpen,
  panel,
  onClose,
}) => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();
  const [payments, setPayments] = useState<PanelPago[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPayments();
    }
  }, [isOpen, panel]);

  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const periodo = extractPeriodFromPanel(panel);
      const periodPayments = await repository.getByPeriodo(periodo);
      setPayments(periodPayments);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al cargar pagos');
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const extractPeriodFromPanel = (currentPanel: PanelFacturacion): string => {
    const date = new Date(currentPanel.fechaInicioPeriodo);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const filteredPayments = useMemo(() => {
    if (!searchTerm) return payments;
    const term = searchTerm.toLowerCase();
    return payments.filter(
      (payment) =>
        payment.nombreClinica.toLowerCase().includes(term) ||
        payment.estadoPagoDescripcion.toLowerCase().includes(term) ||
        (payment.notas && payment.notas.toLowerCase().includes(term)) ||
        payment.monto.toString().includes(term)
    );
  }, [payments, searchTerm]);

  const metrics = useMemo(() => {
    const withPayment = filteredPayments.filter((p) => p.fechaPago);
    const withoutPayment = filteredPayments.filter((p) => !p.fechaPago);
    const totalRecaudado = withPayment.reduce((sum, p) => sum + p.monto, 0);
    const totalPendiente = withoutPayment.reduce((sum, p) => sum + p.monto, 0);
    const total = totalRecaudado + totalPendiente;
    const porcentajeRecaudado = total > 0 ? (totalRecaudado / total) * 100 : 0;
    const promedioPago = withPayment.length > 0 ? totalRecaudado / withPayment.length : 0;

    return {
      cantidadQueAbonaron: withPayment.length,
      cantidadConDeuda: withoutPayment.length,
      totalRecaudado,
      totalPendiente,
      porcentajeRecaudado,
      promedioPago,
    };
  }, [filteredPayments]);

  const paymentMethodBreakdown = useMemo(() => {
    const breakdown: Record<string, { count: number; total: number }> = {};
    filteredPayments.forEach((payment) => {
      if (payment.fechaPago && payment.medioPagoDescripcion) {
        const method = payment.medioPagoDescripcion;
        if (!breakdown[method]) {
          breakdown[method] = { count: 0, total: 0 };
        }
        breakdown[method].count++;
        breakdown[method].total += payment.monto;
      }
    });
    return breakdown;
  }, [filteredPayments]);

  const exportMetrics = useMemo(() => {
    const withPayment = payments.filter((p) => p.fechaPago);
    const withoutPayment = payments.filter((p) => !p.fechaPago);
    const totalRecaudado = withPayment.reduce((sum, p) => sum + p.monto, 0);
    const totalPendiente = withoutPayment.reduce((sum, p) => sum + p.monto, 0);
    const total = totalRecaudado + totalPendiente;
    const porcentajeRecaudado = total > 0 ? (totalRecaudado / total) * 100 : 0;
    const promedioPago = withPayment.length > 0 ? totalRecaudado / withPayment.length : 0;

    return {
      cantidadQueAbonaron: withPayment.length,
      cantidadConDeuda: withoutPayment.length,
      totalRecaudado,
      totalPendiente,
      porcentajeRecaudado,
      promedioPago,
    };
  }, [payments]);

  const formatDateRange = (inicio: Date, fin: Date): string => {
    const inicioStr = inicio.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    const finStr = fin.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    return `${inicioStr} - ${finStr}`;
  };

  const formatMoney = (amount: number): string =>
    `$${amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

  const formatDate = (date: Date | null): string =>
    date ? new Date(date).toLocaleDateString('es-AR') : '-';

  const exportFileName = (extension: string): string =>
    `facturacion-historica-${panel.periodo}.${extension}`;

  const escapeHtml = (value: string | number | null | undefined): string =>
    String(value ?? '-')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const exportRows = () =>
    payments.map((payment) => ({
      clinica: payment.nombreClinica,
      fechaPago: formatDate(payment.fechaPago),
      monto: payment.monto,
      plan: payment.planId ? `${t('pages.billing.plan')} #${payment.planId}` : '-',
      medioPago: payment.medioPagoDescripcion || '-',
      estado: payment.estadoPagoDescripcion,
      notas: payment.notas || '-',
    }));

  const xlsxCell = (
    value: string | number | boolean | null | undefined,
    style: 'title' | 'header' | 'label' | 'money',
    align?: XlsxCellAlign
  ): XlsxCell => ({
    value,
    style,
    align,
  });

  const exportSummaryRows = (dateRange: string): XlsxCell[][] => [
    [xlsxCell(t('pages.billing.exportReportTitle', { periodo: panel.periodo }), 'title')],
    [],
    [
      xlsxCell(t('pages.billing.period'), 'header'),
      xlsxCell(t('pages.billing.dateRange'), 'header'),
      xlsxCell(t('pages.billing.activeShort'), 'header'),
      xlsxCell(t('pages.billing.paidShort'), 'header'),
      xlsxCell(t('pages.billing.collected'), 'header'),
      xlsxCell(t('pages.billing.pending'), 'header'),
      xlsxCell(t('pages.billing.effectiveness'), 'header'),
    ],
    [
      xlsxCell(panel.periodo, 'default', 'center'),
      xlsxCell(dateRange, 'default', 'right'),
      xlsxCell(panel.cantidadClinicasActivas, 'default', 'center'),
      xlsxCell(panel.cantidadClinicasQueAbonaron, 'default', 'center'),
      xlsxCell(panel.dineroRecaudado, 'money', 'center'),
      xlsxCell(panel.dineroPendientePago, 'money', 'center'),
      xlsxCell(`${panel.getEfectividad().toFixed(1)}%`, 'default', 'center'),
    ],
    [],
    [
      xlsxCell(t('pages.billing.exportGeneratedAt'), 'label'),
      xlsxCell(new Date().toLocaleString('es-AR'), 'default', 'right'),
    ],
    [],
    [
      xlsxCell(t('pages.billing.paidClinics'), 'label'),
      xlsxCell(exportMetrics.cantidadQueAbonaron, 'default', 'right'),
    ],
    [
      xlsxCell(t('pages.billing.debtClinics'), 'label'),
      xlsxCell(exportMetrics.cantidadConDeuda, 'default', 'right'),
    ],
    [xlsxCell(t('pages.billing.totalCollected'), 'label'), xlsxCell(exportMetrics.totalRecaudado, 'money')],
    [xlsxCell(t('pages.billing.totalPending'), 'label'), xlsxCell(exportMetrics.totalPendiente, 'money')],
    [
      xlsxCell(t('pages.billing.collectedPercentage'), 'label'),
      xlsxCell(`${exportMetrics.porcentajeRecaudado.toFixed(1)}%`, 'default', 'right'),
    ],
    [xlsxCell(t('pages.billing.averagePayment'), 'label'), xlsxCell(exportMetrics.promedioPago, 'money')],
  ];

  const exportPaymentRows = (): XlsxCell[][] => [
    [
      xlsxCell(t('pages.billing.clinic'), 'header'),
      xlsxCell(t('pages.billing.paymentDate'), 'header'),
      xlsxCell(t('pages.billing.amount'), 'header'),
      xlsxCell(t('pages.billing.plan'), 'header'),
      xlsxCell(t('pages.billing.paymentMethod'), 'header'),
      xlsxCell(t('pages.billing.status'), 'header'),
      xlsxCell(t('pages.billing.notes'), 'header'),
    ],
    ...exportRows().map((row) => [
      row.clinica,
      row.fechaPago,
      xlsxCell(row.monto, 'money'),
      row.plan,
      row.medioPago,
      row.estado,
      row.notas,
    ]),
  ];

  const buildExportHtml = (): string => {
    const dateRange = formatDateRange(
      new Date(panel.fechaInicioPeriodo),
      new Date(panel.fechaFinPeriodo)
    );
    const rows = exportRows();
    const now = new Date().toLocaleString('es-AR');

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(t('pages.billing.exportReportTitle', { periodo: panel.periodo }))}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
    h1 { font-size: 20px; margin: 0 0 6px; }
    .muted { color: #64748b; font-size: 12px; margin-bottom: 18px; }
    .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 18px; }
    .metric { border: 1px solid #d7dee8; border-radius: 8px; padding: 10px; }
    .metric span { display: block; color: #64748b; font-size: 11px; margin-bottom: 4px; }
    .metric strong { font-size: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #d7dee8; padding: 8px; text-align: left; vertical-align: top; }
    th { background: #eef2f7; color: #334155; text-transform: uppercase; font-size: 10px; }
    .num { text-align: right; }
  </style>
</head>
<body>
  <h1>${escapeHtml(t('pages.billing.exportReportTitle', { periodo: panel.periodo }))}</h1>
  <div class="muted">${escapeHtml(t('pages.billing.dateRange'))}: ${escapeHtml(dateRange)} · ${escapeHtml(t('pages.billing.exportGeneratedAt'))}: ${escapeHtml(now)}</div>
  <table style="margin-bottom: 18px;">
    <thead>
      <tr>
        <th>${escapeHtml(t('pages.billing.period'))}</th>
        <th>${escapeHtml(t('pages.billing.dateRange'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.activeShort'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.paidShort'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.collected'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.pending'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.effectiveness'))}</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${escapeHtml(panel.periodo)}</td>
        <td>${escapeHtml(dateRange)}</td>
        <td class="num">${panel.cantidadClinicasActivas}</td>
        <td class="num">${panel.cantidadClinicasQueAbonaron}</td>
        <td class="num">${escapeHtml(formatMoney(panel.dineroRecaudado))}</td>
        <td class="num">${escapeHtml(formatMoney(panel.dineroPendientePago))}</td>
        <td class="num">${panel.getEfectividad().toFixed(1)}%</td>
      </tr>
    </tbody>
  </table>
  <div class="summary">
    <div class="metric"><span>${escapeHtml(t('pages.billing.paidClinics'))}</span><strong>${exportMetrics.cantidadQueAbonaron}</strong></div>
    <div class="metric"><span>${escapeHtml(t('pages.billing.debtClinics'))}</span><strong>${exportMetrics.cantidadConDeuda}</strong></div>
    <div class="metric"><span>${escapeHtml(t('pages.billing.totalCollected'))}</span><strong>${escapeHtml(formatMoney(exportMetrics.totalRecaudado))}</strong></div>
    <div class="metric"><span>${escapeHtml(t('pages.billing.totalPending'))}</span><strong>${escapeHtml(formatMoney(exportMetrics.totalPendiente))}</strong></div>
    <div class="metric"><span>${escapeHtml(t('pages.billing.collectedPercentage'))}</span><strong>${exportMetrics.porcentajeRecaudado.toFixed(1)}%</strong></div>
    <div class="metric"><span>${escapeHtml(t('pages.billing.averagePayment'))}</span><strong>${escapeHtml(formatMoney(exportMetrics.promedioPago))}</strong></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>${escapeHtml(t('pages.billing.clinic'))}</th>
        <th>${escapeHtml(t('pages.billing.paymentDate'))}</th>
        <th class="num">${escapeHtml(t('pages.billing.amount'))}</th>
        <th>${escapeHtml(t('pages.billing.plan'))}</th>
        <th>${escapeHtml(t('pages.billing.paymentMethod'))}</th>
        <th>${escapeHtml(t('pages.billing.status'))}</th>
        <th>${escapeHtml(t('pages.billing.notes'))}</th>
      </tr>
    </thead>
    <tbody>
      ${
        rows.length > 0
          ? rows
              .map(
                (row) => `<tr>
        <td>${escapeHtml(row.clinica)}</td>
        <td>${escapeHtml(row.fechaPago)}</td>
        <td class="num">${escapeHtml(formatMoney(row.monto))}</td>
        <td>${escapeHtml(row.plan)}</td>
        <td>${escapeHtml(row.medioPago)}</td>
        <td>${escapeHtml(row.estado)}</td>
        <td>${escapeHtml(row.notas)}</td>
      </tr>`
              )
              .join('')
          : `<tr><td colspan="7">${escapeHtml(t('pages.billing.noPayments'))}</td></tr>`
      }
    </tbody>
  </table>
</body>
</html>`;
  };

  const handleExportPdf = () => {
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      showError(t('pages.billing.exportPopupBlocked'));
      return;
    }

    reportWindow.document.write(buildExportHtml());
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
  };

  const handleExportExcel = () => {
    const dateRange = formatDateRange(
      new Date(panel.fechaInicioPeriodo),
      new Date(panel.fechaFinPeriodo)
    );
    const blob = createXlsxBlob([
      {
        name: t('pages.billing.summary'),
        rows: exportSummaryRows(dateRange),
        columns: [30, 18, 12, 12, 14, 14, 14],
      },
      {
        name: t('pages.billing.payments'),
        rows: exportPaymentRows(),
        columns: [32, 14, 14, 18, 18, 16, 36],
      },
    ]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFileName('xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const getStatusBadgeClass = (estado: string) => {
    if (estado === 'Abonado') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (estado === 'Mora') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (estado === 'Impago') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (estado === 'Gratis') return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const handleCancel = async (paymentId: number) => {
    if (!window.confirm(t('pages.billing.cancelPaymentConfirm'))) return;
    setCancellingId(paymentId);
    try {
      const action = container.get<CancelPanelPagoAction>(TYPES.CancelPanelPagoAction);
      await action.execute(paymentId);
      success(t('pages.billing.cancelPaymentSuccess'));
      await loadPayments();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={`${t('pages.billing.details')} - ${panel.periodo}`}
      subtitle={
        <>
          <strong>{t('pages.billing.dateRange')}:</strong>{' '}
          {formatDateRange(
            new Date(panel.fechaInicioPeriodo),
            new Date(panel.fechaFinPeriodo)
          )}
        </>
      }
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="secondary"
              onClick={handleExportPdf}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {t('pages.billing.exportPdf')}
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportExcel}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {t('pages.billing.exportExcel')}
            </Button>
          </div>
          <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
            {t('common.close')}
          </Button>
        </div>
      }
    >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.billing.paidClinics')}</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  {metrics.cantidadQueAbonaron}
                </p>
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.billing.debtClinics')}</p>
                <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                  {metrics.cantidadConDeuda}
                </p>
              </div>
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('pages.billing.totalCollected')}
                </p>
                <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                  ${metrics.totalRecaudado.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.billing.totalPending')}</p>
                <p className="text-lg font-bold text-red-600 dark:text-red-400">
                  ${metrics.totalPendiente.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('pages.billing.collectedPercentage')}
                </p>
                <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                  {metrics.porcentajeRecaudado.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('pages.billing.averagePayment')}
                </p>
                <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  ${metrics.promedioPago.toLocaleString()}
                </p>
              </div>
            </div>

            {Object.keys(paymentMethodBreakdown).length > 0 && (
              <div className="mb-6 p-4 bg-slate-50 dark:bg-gray-900/50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t('pages.billing.paymentMethodBreakdown')}
                </h4>
                <div className="space-y-2">
                  {Object.entries(paymentMethodBreakdown).map(([method, data]) => (
                    <div key={method} className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{method}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {data.count} {t('pages.billing.payments')} - ${data.total.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <Input
                label={t('common.search')}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('pages.billing.searchPayments')}
              />
            </div>

            {isLoading ? (
              <div className="text-center py-4">
                <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                {t('pages.billing.noPayments')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-slate-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.clinic')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.paymentDate')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.amount')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.plan')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.paymentMethod')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.billing.notes')}
                      </th>
                      {canAdminister && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                          {t('common.actions')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {payment.nombreClinica}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.fechaPago
                            ? new Date(payment.fechaPago).toLocaleDateString('es-AR')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                          ${payment.monto.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.planId || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.medioPagoDescripcion || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.estadoPagoDescripcion)}`}
                          >
                            {payment.estadoPagoDescripcion}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.notas || '-'}
                        </td>
                        {canAdminister && (
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={cancellingId === payment.id || !payment.fechaPago}
                              onClick={() => handleCancel(payment.id)}
                              title={
                                payment.fechaPago
                                  ? t('pages.billing.cancelPayment')
                                  : t('pages.billing.cancelPaymentDisabled')
                              }
                            >
                              {cancellingId === payment.id
                                ? t('pages.billing.cancelPaymentRunning')
                                : t('pages.billing.cancelPayment')}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
    </ModalShell>
  );
};
